import React, { useEffect, useMemo, useState } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import { buildHierarchyTree } from '../utils/hierarchyTree';

/**
 * Collapsible multi-select for picking many rows from a lookup table.
 *
 * Shared by the Hierarchy / Regulatory Reference / Policy Area / Applicability
 * Group pickers so they stay visually and behaviourally identical. Pass a
 * `fetchItems` function returning an axios promise; it runs once on mount.
 *
 * `tree` is for the hierarchy case only: while the filter box is empty, rows
 * are indented by depth and labelled with just their own name, so "Acme
 * Healthcare Organization / Memorial Hospital / ..." doesn't repeat on every
 * row underneath Memorial Hospital. Typing a filter falls back to the flat
 * `labelKey` (the full breadcrumb) -- indentation stops helping once matches
 * span unrelated branches, and the breadcrumb is what confirms *which* branch
 * matched.
 */
function LookupMultiSelect({
  fetchItems, selectedIds, onChange, emptyMessage, labelKey = 'name',
  placeholder = 'Select...', tree = false, treeLabelKey = 'name',
  items: providedItems = null, searchKeys = null, metaKey = null,
}) {
  const [fetchedItems, setFetchedItems] = useState([]);
  const [loading, setLoading] = useState(providedItems === null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');

  // `items` lets a caller that ALREADY has the rows pass them straight in
  // (issue #102's standards picker: DraftingAssistantPanel fetches the same endpoint
  // to decide whether the library is empty at all, and fetching it a second
  // time here purely to populate a dropdown would be the N+1 shape this
  // component exists to avoid, in miniature).
  const items = providedItems === null ? fetchedItems : providedItems;

  useEffect(() => {
    if (providedItems !== null) return undefined;
    let cancelled = false;
    fetchItems()
      .then((res) => {
        if (!cancelled) setFetchedItems(res.data);
      })
      .catch((err) => {
        console.error('Error loading options:', err);
        // Distinct from a genuinely empty table (issue #150): a 403/network
        // failure used to fall through to `emptyMessage`, which reads
        // "nothing defined yet -- add one under Admin" -- actively misleading
        // for a request that was refused rather than answered honestly.
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const treeItems = useMemo(() => (tree ? buildHierarchyTree(items) : items), [tree, items]);
  const trimmedQuery = query.trim().toLowerCase();
  const searching = trimmedQuery.length > 0;
  // `searchKeys` widens matching beyond the visible label -- a standard is
  // findable by its publisher or jurisdiction ("CMS", "California") without
  // those having to be crammed into the label itself.
  const matchKeys = searchKeys || [labelKey];
  const visible = searching
    ? items.filter((item) => matchKeys.some(
        (key) => String(item[key] || '').toLowerCase().includes(trimmedQuery)))
    : treeItems;

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((existing) => existing !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (loading) {
    return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Loading options...</div>;
  }

  if (loadError) {
    return <div style={{ fontSize: '13px', color: 'var(--danger)' }}>Could not load options.</div>;
  }

  if (items.length === 0) {
    return <div style={{ fontSize: '13px', color: 'var(--muted)' }}>{emptyMessage}</div>;
  }

  const selected = items.filter((item) => selectedIds.includes(item.id));
  const summary = selected.length === 0
    ? placeholder
    : selected.length <= 2
      ? selected.map((item) => item[tree ? treeLabelKey : labelKey]).join(', ')
      : `${selected.length} selected`;

  return (
    <MultiSelectDropdown label={summary}>
      <input
        type="text"
        className="multiselect-search"
        placeholder="Filter..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className="multiselect-options" role="listbox" aria-multiselectable="true">
        {visible.map((item) => (
          <label
            key={item.id}
            className="multiselect-option"
            style={tree && !searching ? { paddingLeft: `${10 + item.depth * 16}px` } : undefined}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(item.id)}
              onChange={() => toggle(item.id)}
            />
            <span>
              {tree && !searching ? item[treeLabelKey] : item[labelKey]}
              {metaKey && item[metaKey] && (
                <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--muted)' }}>
                  {item[metaKey]}
                </span>
              )}
            </span>
          </label>
        ))}
        {visible.length === 0 && (
          <div style={{ fontSize: '13px', color: 'var(--muted)', padding: '6px 8px' }}>No matches.</div>
        )}
      </div>
      {selectedIds.length > 0 && (
        <button type="button" className="multiselect-clear" onClick={() => onChange([])}>
          Clear selection
        </button>
      )}
    </MultiSelectDropdown>
  );
}

export default LookupMultiSelect;
